import { ref, watch } from 'vue'
import Watermark from '@/utils/useWatermark'
import { fetchWatermarkConfig } from '@/api/watermark'
import { useAuthStore } from '@/stores/login'

interface WatermarkConfig {
  isWatermark?: number
  showUserName?: boolean
  showUserUsername?: boolean
  showUserCode?: boolean
  showDate?: boolean
  showDeptName?: boolean
  showPostName?: boolean
  gapX?: number
  gapY?: number
  fontSize?: number
  rotate?: number
  opacity?: number
  color?: string
}

interface WatermarkUserInfo {
  name?: string
  username?: string
  nickName?: string
  postName?: string
  deptName?: string
  deptFullName?: string
}

export function useWatermarkManager() {
  const watermark = ref<Watermark | null>(null)
  const configTimer = ref<ReturnType<typeof setInterval> | null>(null)
  const watermarkRequested = ref(false)
  const authStore = useAuthStore()

  function toBoolean(value: unknown) {
    return value === true || value === 1 || value === '1'
  }

  function safeParseUserInfoFromCache(): WatermarkUserInfo | null {
    try {
      const raw = localStorage.getItem('SandsTalkUserInfo')
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return null

      const obj = parsed as Record<string, unknown>
      const usernameCandidate =
        (obj.username as string) ||
        (obj.empNo as string) ||
        (obj.userName as string) ||
        (obj.identifierValue as string) ||
        ''

      return {
        name: (obj.name as string) || '',
        username: usernameCandidate,
        nickName: (obj.nickName as string) || (obj.nickname as string) || '',
        postName: (obj.postName as string) || '',
        deptName: (obj.deptName as string) || '',
        deptFullName: (obj.deptFullName as string) || ''
      }
    } catch {
      return null
    }
  }

  // 从 store 中获取用户信息
  function getUserInfoFromStore(): WatermarkUserInfo | null {
    try {
      const cached = safeParseUserInfoFromCache()

      const infoFromStore: WatermarkUserInfo = {
        name: authStore.getName || '',
        username: authStore.getUsername || '',
        nickName: authStore.getNickName || '',
        postName: authStore.getPostName || '',
        deptName: authStore.getDeptName || '',
        deptFullName: authStore.getDeptFullName || ''
      }

      return {
        name: infoFromStore.name || cached?.name || '',
        username: cached?.username || infoFromStore.username || '',
        nickName: infoFromStore.nickName || cached?.nickName || '',
        postName: infoFromStore.postName || cached?.postName || '',
        deptName: infoFromStore.deptName || cached?.deptName || '',
        deptFullName: infoFromStore.deptFullName || cached?.deptFullName || ''
      }
    } catch {
      return null
    }
  }

  // 根据配置生成水印文本（支持多行）
  function generateWatermarkText(config: WatermarkConfig): string | string[] {
    const userInfo = getUserInfoFromStore()

    const texts: string[] = []

    if (config.showUserName && userInfo?.name) {
      texts.push(userInfo.name)
    }

    if (config.showUserUsername && userInfo?.username) {
      texts.push(userInfo.username)
    }

    if (config.showUserCode && userInfo?.nickName) {
      texts.push(userInfo.nickName)
    }

    if (config.showPostName && userInfo?.postName) {
      texts.push(userInfo.postName)
    }

    if (config.showDeptName && userInfo?.deptName) {
      texts.push(userInfo.deptName)
    }

    if (config.showDate) {
      texts.push(new Date().toLocaleString())
    }

    if (texts.length === 0) {
      return ''
    }

    return texts.join(' - ')
  }

  // 加载水印配置
  async function loadWatermarkConfig(): Promise<WatermarkConfig> {
    try {
      const res = await fetchWatermarkConfig()
      const rawData = (res as any)?.data
      const configData =
        rawData && typeof rawData === 'object' && !Array.isArray(rawData)
          ? (rawData as any).data && typeof (rawData as any).data === 'object'
            ? (rawData as any).data
            : rawData
          : {}

      // 根据屏幕尺寸调整参数
      const isMobile = window.innerWidth < 768
      const defaultGapX = isMobile ? 120 : 200
      const defaultGapY = isMobile ? 100 : 150
      const defaultFontSize = 14

      return {
        isWatermark: Number((configData as any).isWatermark || 0),
        showUserName: toBoolean((configData as any).showUserName),
        showUserUsername: toBoolean((configData as any).showUserUsername),
        showUserCode: toBoolean((configData as any).showUserCode),
        showDate: toBoolean((configData as any).showDate),
        showDeptName: toBoolean((configData as any).showDeptName),
        showPostName: toBoolean((configData as any).showPostName),
        gapX: defaultGapX,
        gapY: defaultGapY,
        fontSize: defaultFontSize,
        rotate: -30,
        opacity: 0.15,
        color: 'rgb(128,128,128)'
      }
    } catch (error) {
      // 根据屏幕尺寸调整参数
      const isMobile = window.innerWidth < 768
      const defaultGapX = isMobile ? 120 : 200
      const defaultGapY = isMobile ? 100 : 150
      const defaultFontSize = 14

      return {
        isWatermark: 0,
        showUserName: false,
        showUserUsername: false,
        showUserCode: false,
        showDate: false,
        showDeptName: false,
        showPostName: false,
        gapX: defaultGapX,
        gapY: defaultGapY,
        fontSize: defaultFontSize,
        rotate: -30,
        opacity: 0.15,
        color: 'rgb(128,128,128)'
      }
    }
  }

  // 创建水印
  async function createWatermark() {
    watermarkRequested.value = true
    const token = localStorage.getItem('sandsTalkToken') || authStore.getAccessToken
    if (!token) return

    destroyWatermark()

    const config = await loadWatermarkConfig()
    const isMobile = window.innerWidth < 768

    // 如果未开启水印，则不创建
    if (config.isWatermark !== 1) {
      return
    }

    const scale = isMobile ? 3 : 1
    const minGapX = Math.ceil(window.innerWidth / scale)
    const resolvedGapX = Math.max(config.gapX || 500, minGapX)

    const text = generateWatermarkText(config)
    watermark.value = new Watermark({
      text,
      font: `${config.fontSize || 14}px Arial`,
      color: config.color || 'rgb(128,128,128)',
      rotate: config.rotate || -45,
      opacity: config.opacity || 0.1,
      gapX: resolvedGapX,
      gapY: config.gapY || 150,
      scale,
      zIndex: 9999
    })

    watermark.value.create()

    // 每分钟更新一次时间水印
    if (config.showDate) {
      configTimer.value = setInterval(() => {
        const newText = generateWatermarkText(config)
        if (watermark.value) {
          watermark.value.update(newText)
        }
      }, 60000)
    }
  }

  // 销毁水印
  function destroyWatermark() {
    if (watermark.value) {
      watermark.value.destroy()
      watermark.value = null
    }

    if (configTimer.value) {
      clearInterval(configTimer.value)
      configTimer.value = null
    }
  }

  watch(
    () => authStore.sandsTalkToken,
    (token) => {
      if (!watermarkRequested.value) return
      if (token) {
        createWatermark()
      } else {
        destroyWatermark()
      }
    }
  )

  return {
    createWatermark,
    destroyWatermark
  }
}
